import { __awaiter, __rest } from "tslib";
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { context as otelContext } from '@opentelemetry/api';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { ATTRIBUTE_ACTIVITY_TYPE, ATTRIBUTE_EVENT_NAME, PRODUCT_LOGGER_NAME, } from './metrics.constants';
import { ActivityType } from './metrics.types';
import { getDataDogIdentifiers } from './trace';
function matchesAnySaasHost(message) {
    const urlRegex = /https?:\/\/[^\s"'<>]+/g;
    const allowedHosts = [
        'api.segment.io',
        'app.launchdarkly.com',
        'api-iam.intercom.io',
        'events.launchdarkly.com',
        'otel.elastic.seequent.com',
        'www.google-analytics.com',
    ];
    const matches = message.match(urlRegex);
    if (matches) {
        for (const foundUrl of matches) {
            try {
                const parsed = new URL(foundUrl);
                if (allowedHosts.includes(parsed.host)) {
                    return true;
                }
            }
            catch (_a) {
            }
        }
    }
    return false;
}
function shouldLogError(event) {
    var _a, _b, _c, _d;
    if ((_a = event === null || event === void 0 ? void 0 : event.message) === null || _a === void 0 ? void 0 : _a.includes('Object Not Found Matching Id')) {
        return false;
    }
    if (((event === null || event === void 0 ? void 0 : event.error) &&
        (event === null || event === void 0 ? void 0 : event.message) &&
        (matchesAnySaasHost(event.message) ||
            ((_b = event.message) === null || _b === void 0 ? void 0 : _b.includes('ResizeObserver loop completed')) ||
            ((_c = event.message) === null || _c === void 0 ? void 0 : _c.includes('access_token=')))) ||
        ((_d = event.message) === null || _d === void 0 ? void 0 : _d.includes('RestError: The specified blob does not exist.'))) {
        return false;
    }
    return true;
}
export const initializeMetrics = (config) => __awaiter(void 0, void 0, void 0, function* () {
    const { datadogConfig, env, segmentWriteKey } = config;
    if (segmentWriteKey && !window.segmentAnalytics) {
        const [analytics] = yield AnalyticsBrowser.load({ writeKey: segmentWriteKey });
        window.segmentAnalytics = analytics;
    }
    if (datadogConfig && !window.datadog) {
        const enableExperimentalFeatures = [];
        if (datadogConfig.sendFeatureFlags) {
            enableExperimentalFeatures.push('feature_flags');
        }
        const { appId, beforeSend, clientToken, enableSessionReplays, forwardConsoleLogs, forwardErrorsToLogs, service, version, } = datadogConfig;
        datadogRum.init({
            applicationId: appId,
            clientToken,
            site: 'datadoghq.com',
            env: env !== null && env !== void 0 ? env : 'undefined',
            version,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            defaultPrivacyLevel: 'mask',
            actionNameAttribute: 'automation-id',
            telemetrySampleRate: 0,
            useSecureSessionCookie: true,
            compressIntakeRequests: true,
            beforeSend,
            enableExperimentalFeatures,
        });
        datadogLogs.init({
            clientToken,
            site: 'datadoghq.com',
            env: config.env || 'undefined',
            forwardErrorsToLogs: forwardErrorsToLogs !== null && forwardErrorsToLogs !== void 0 ? forwardErrorsToLogs : true,
            forwardConsoleLogs: forwardConsoleLogs !== null && forwardConsoleLogs !== void 0 ? forwardConsoleLogs : ['error'],
            sessionSampleRate: 100,
            beforeSend: shouldLogError,
            service,
            version,
        });
        if (config.env === 'development' || enableSessionReplays) {
            datadogRum.startSessionReplayRecording();
        }
        window.datadog = true;
    }
});
export const setUserContext = (userContext, featureFlags) => __awaiter(void 0, void 0, void 0, function* () {
    let { details } = userContext;
    if (window.segmentAnalytics && userContext.userId) {
        if (userContext.orgId && (details === null || details === void 0 ? void 0 : details.orgId) === undefined) {
            details = Object.assign(Object.assign({}, details), { orgId: userContext.orgId });
        }
        yield window.segmentAnalytics.identify(userContext.userId, details);
    }
    if (window.datadog && userContext.userId) {
        datadogRum.setUser(Object.assign({ id: userContext.userId, orgId: userContext.orgId }, details));
        if (featureFlags) {
            Object.entries(featureFlags).forEach(([key, value]) => {
                datadogRum.addFeatureFlagEvaluation(key, value);
            });
        }
    }
});
export const clearUserContext = () => {
    if (window.datadog) {
        const hasUser = datadogRum.getUser();
        if (Object.keys(hasUser).length) {
            datadogRum.stopSession();
            datadogRum.clearUser();
        }
    }
};
export const trackUserAction = (action, details) => __awaiter(void 0, void 0, void 0, function* () {
    if (window.segmentAnalytics) {
        yield window.segmentAnalytics.track(action, details);
    }
    if (window.datadog) {
        datadogRum.addAction(`${action}`, details);
    }
    if (window.otelTracer) {
        const logger = logs.getLogger('userAction');
        const attributes = Object.assign(Object.assign({}, details), getDataDogIdentifiers());
        const body = {
            name: PRODUCT_LOGGER_NAME,
            [ATTRIBUTE_EVENT_NAME]: action,
        };
        logger.emit({
            severityNumber: SeverityNumber.INFO,
            severityText: 'info',
            body,
            attributes,
            context: otelContext.active(),
        });
    }
});
export const trackPage = () => __awaiter(void 0, void 0, void 0, function* () {
    if (window.segmentAnalytics) {
        yield window.segmentAnalytics.page();
    }
});
export const trackError = (error, message, details, severityNumber) => {
    var _a;
    if (window.datadog) {
        const context = { message, details };
        datadogRum.addError(error, context);
    }
    if (window.otelTracer) {
        const logger = logs.getLogger('error');
        const attributes = Object.assign({}, details);
        if (typeof error === 'object') {
            Object.assign(attributes, error);
        }
        const body = { [ATTRIBUTE_EVENT_NAME]: typeof error === 'string' ? error : message };
        if (!message) {
            body[ATTRIBUTE_EVENT_NAME] = (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : 'Unknown error';
        }
        logger.emit({
            severityNumber: severityNumber !== null && severityNumber !== void 0 ? severityNumber : SeverityNumber.ERROR,
            severityText: 'error',
            body,
            attributes,
            context: otelContext.active(),
        });
    }
};
export var LogLevel;
(function (LogLevel) {
    LogLevel["debug"] = "debug";
    LogLevel["info"] = "info";
    LogLevel["warn"] = "warn";
    LogLevel["error"] = "error";
})(LogLevel || (LogLevel = {}));
export function inferMetricActivity(actionName) {
    if (actionName.startsWith('create')) {
        return ActivityType.Create;
    }
    if (actionName.startsWith('update')) {
        return ActivityType.Update;
    }
    if (actionName.startsWith('upload') || actionName.startsWith('import')) {
        return ActivityType.Import;
    }
    if (actionName.startsWith('download') || actionName.startsWith('export')) {
        return ActivityType.Export;
    }
    if (actionName.startsWith('restore')) {
        return ActivityType.Restore;
    }
    if (actionName.startsWith('softDelete')) {
        return ActivityType.SoftDelete;
    }
    if (actionName.startsWith('delete')) {
        return ActivityType.Delete;
    }
    if (actionName.includes('load') || actionName.includes('view')) {
        return ActivityType.View;
    }
    return ActivityType.Unknown;
}
export const customLog = (message, context, level = LogLevel.info, error, severityNumber) => {
    var _a;
    if (window.datadog) {
        datadogLogs.logger.log(message, context, level, error);
    }
    if (window.otelTracer) {
        const severityText = level.toString();
        const logger = logs.getLogger(PRODUCT_LOGGER_NAME);
        const actionType = inferMetricActivity(message);
        const attributes = Object.assign(Object.assign(Object.assign({}, context), getDataDogIdentifiers()), { [ATTRIBUTE_ACTIVITY_TYPE]: actionType });
        if (error) {
            Object.assign(attributes, error);
        }
        let msgObject = {};
        try {
            msgObject = JSON.parse(message);
        }
        catch (_b) {
            msgObject = { metric: message };
        }
        finally {
            if (!Object.getOwnPropertyNames(msgObject).includes('metric')) {
                msgObject.metric = 'unknown_event';
            }
            const { metric: _ } = msgObject, remaining = __rest(msgObject, ["metric"]);
            Object.assign(attributes, remaining);
        }
        const body = {
            name: PRODUCT_LOGGER_NAME,
            [ATTRIBUTE_EVENT_NAME]: (_a = msgObject.metric) !== null && _a !== void 0 ? _a : message,
        };
        logger.emit({
            severityNumber: severityNumber !== null && severityNumber !== void 0 ? severityNumber : mapLogLevelToSeverityNumber(level),
            severityText,
            body,
            attributes,
            context: otelContext.active(),
        });
    }
};
export function getRumRecordingUrl() {
    if (!window.datadog) {
        return undefined;
    }
    try {
        return datadogRum.getSessionReplayLink();
    }
    catch (_a) {
        return undefined;
    }
}
export function getOtelLogger(loggerName, version, options) {
    if (window.otelTracer) {
        const dataDogIds = (window === null || window === void 0 ? void 0 : window.datadog) ? getDataDogIdentifiers() : undefined;
        return logs.getLogger(loggerName, version, options !== null && options !== void 0 ? options : { scopeAttributes: dataDogIds });
    }
    return undefined;
}
function mapLogLevelToSeverityNumber(level) {
    switch (level) {
        case LogLevel.debug:
            return SeverityNumber.DEBUG;
        case LogLevel.warn:
            return SeverityNumber.WARN;
        case LogLevel.error:
            return SeverityNumber.ERROR;
        case LogLevel.info:
        default:
            return SeverityNumber.INFO;
    }
}
//# sourceMappingURL=metrics.js.map